import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { DragHandleIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, Menu, MenuButton, MenuItem, MenuList, Badge } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import RemoveIconComponent from 'components/Icons/RemoveIcon';
import { getDashboardBlocks } from 'redux/DashboardSliceV2';
import FigmaDimensionsIcon from 'components/Icons/FigmaDimensionsIcon';

import CustomToast from '../../../components/CustomToast';

export interface Properties {
  indicator: any;
  selectDimension: (dimension: any) => void;
  removeIndicator: (indicator_id: any) => void;
  showPopup: boolean;
  flags?: any;
}

const IndicatorRow = ({
  indicator,
  selectDimension,
  removeIndicator,
  showPopup,
  flags,
}: Properties) => {
  const [displayPopup, setDisplayPopup] = useState(false);
  const blockDetails = useSelector(getDashboardBlocks);
  const [hover, setHover] = useState<boolean>(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: indicator?.indicator_id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)',
    zIndex: isDragging ? 200 : 'auto',
    opacity: isDragging ? 0 : 1,
    pointerEvents: isDragging ? ('none' as const) : ('auto' as const),
  };
  const selectedBlock = useMemo(() => {
    if (indicator?.block_id) {
      const blockData = blockDetails?.find(
        (item: any) => item.block_id === Number(indicator.block_id),
      );
      return blockData?.block_name || 'Select block';
    }
    return null;
  }, [indicator?.block_id, blockDetails]);

  const blockIndicators = useMemo(() => {
    if (indicator?.block_id) {
      const blockData = blockDetails?.find(
        (item: any) => item.block_id === Number(indicator.block_id),
      );
      return blockData?.indicators || [] || [] || [];
    }
    return [];
  }, [indicator?.block_id, blockDetails]);

  const dimensions = useMemo(() => {
    if (indicator?.block_id && indicator?.indicator_id) {
      const blockData = blockDetails?.find(
        (item: any) => item.block_id === Number(indicator.block_id),
      );
      if (blockData) {
        const indicatorData = blockData?.indicators?.find(
          (item: any) => item.indicator_id === Number(indicator.indicator_id),
        );

        let returnArray: any[] = [];
        if (indicatorData && indicatorData?.indicator_dimensions?.length) {
          returnArray = (indicatorData.indicator_dimensions ?? []).map((item: any) => ({
            dimension_id: item.indicator_dimension_id,
            dimension_name: item.indicator_dimension_name,
            connected: Boolean(item.connected),
          }));
        }

        if (indicatorData && indicatorData?.connected_indicator_dimensions?.length) {
          const showConnectedDim = flags?.dashboardConnectedDimension;
          if (!showConnectedDim) return returnArray.filter((item) => !item.connected);
        }
        return returnArray;
      }
    }
    return [];
  }, [indicator?.block_id, indicator?.indicator_id, blockDetails]);

  const { selectedDimensionName, selectedDimensionIsConnected = false } = useMemo(() => {
    if (indicator?.dimension_id && blockIndicators?.length) {
      const dimData = dimensions?.find(
        (item: any) => item.dimension_id === Number(indicator.dimension_id),
      );
      return {
        selectedDimensionName: dimData?.dimension_name,
        selectedDimensionIsConnected: dimData?.connected,
      };
    }
    return {
      selectedDimensionName: null,
      selectedDimensionIsConnected: false,
    };
  }, [indicator?.dimension_id, dimensions]);

  const selectedIndicatorName = useMemo(() => {
    if (indicator?.block_id && blockIndicators?.length) {
      const blockData = blockIndicators?.find(
        (item: any) => item.indicator_id === indicator.indicator_id,
      );
      return blockData?.indicator_name || 'Select indicator';
    }
    return null;
  }, [indicator?.indicator_id, blockIndicators]);

  const handleRemoveIndicator = (indicator_id: any) => {
    removeIndicator(indicator_id);
  };

  const handleDimensionSelect = (dim: any) => {
    if (!showPopup) {
      selectDimension(dim);
    } else {
      setDisplayPopup(true);
      setTimeout(() => {
        setDisplayPopup(false);
      }, 3000);
    }
  };

  return (
    <>
      <Box alignItems={'center'} ref={setNodeRef} style={style}>
        {indicator && (
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            gap='8px'
            bgColor={'white'}
            border='1px solid'
            borderColor={isDragging ? '#8B5CF6' : '#D9D9D9'}
            borderRadius={'8px'}
            p='12px'
            boxShadow={isDragging ? '0 8px 24px rgba(139,92,246,0.18)' : 'none'}
            transform={isDragging ? 'scale(1.02)' : 'scale(1)'}
            transition='box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease'
            onMouseOver={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <Flex alignItems={'center'} gap='4px' minW='0'>
              <DragHandleIcon
                display={hover ? 'block' : 'none'}
                {...listeners}
                {...attributes}
                cursor={'grab'}
                color={'#708090'}
                _focus={{ outline: 'none' }}
                height={'12px'}
                flexShrink={0}
              />
              <Box minW='0'>
                <Text
                  fontFamily='Roboto Regular'
                  fontWeight='500'
                  fontSize={'14px'}
                  color='#8B5CF6'
                  textAlign={'left'}
                  textOverflow='ellipsis'
                  whiteSpace={'nowrap'}
                  overflow='hidden'
                >
                  {selectedIndicatorName || 'Select indicator'}
                </Text>
                {indicator?.block_id !== null && (
                  <Text fontFamily='Roboto Regular' fontSize={'12px'} color={'#708090'}>
                    {selectedBlock}
                  </Text>
                )}
              </Box>
            </Flex>
            <Flex alignItems={'center'} gap='11px' flexShrink={0}>
              {dimensions?.length >= 1 && (
                <Menu strategy='fixed'>
                  <MenuButton>
                    <Flex alignItems='center' gap='6px'>
                      <FigmaDimensionsIcon width={14} height={14} color={'#8B5CF6'} />
                      <Text
                        fontFamily='Roboto Regular'
                        fontWeight='500'
                        fontSize={'14px'}
                        color={'#8B5CF6'}
                      >
                        {selectedDimensionName || '+ Add'}
                      </Text>
                      {selectedDimensionIsConnected && (
                        <Badge
                          colorScheme='green'
                          size='sm'
                          ml={1}
                          fontSize='10px'
                          title='Connected'
                        >
                          <Text fontSize='10px'>Connected</Text>
                        </Badge>
                      )}
                    </Flex>
                  </MenuButton>
                  <MenuList>
                    {dimensions?.map((dim: any) => {
                      return (
                        <MenuItem
                          key={dim?.dimension_id}
                          onClick={() => handleDimensionSelect(dim)}
                        >
                          <Flex alignItems='center'>
                            <Text>{dim?.dimension_name}</Text>
                            {dim?.connected && (
                              <Badge
                                colorScheme='green'
                                size='sm'
                                ml={2}
                                fontSize='10px'
                                title='Connected'
                              >
                                <Text fontSize='10px'>Connected</Text>
                              </Badge>
                            )}
                          </Flex>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              )}
              <RemoveIconComponent
                width={14}
                height={14}
                color='#FE552F'
                onClick={() => handleRemoveIndicator(indicator.indicator_id)}
              />
            </Flex>
          </Flex>
        )}
      </Box>
      {displayPopup && (
        <CustomToast
          title='Dimensions not available when displaying multiple indicators'
          status='error'
        />
      )}
    </>
  );
};
export default withLDConsumer()(IndicatorRow);
